import React, {useMemo} from 'react'
import {Box, Typography, Tooltip} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, ORANGE, LOST_RED, withOpacity} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'

/**
 * StatusTree — the "Shipments Status Split" as a clean, self-explanatory tree.
 * Total → status groups → sub-statuses, with a proportion bar on each group so the
 * relative size is visible while every exact count is still spelled out. Replaces the
 * old d3 org-chart (which left a large empty canvas and needed line-tracing to read).
 *
 * `nodes` is the flat org-chart array: [{id, parentId, title, count}] — root has no parentId.
 */
const GROUP_COLORS = {
  'Shipments Placed': PRIMARY_BLUE,
  'In-Transit': ORANGE,
  'Delivered': SUCCESS,
  'RTO': ERROR,
  'Lost': LOST_RED,
}
const GROUP_ORDER = ['Shipments Placed', 'In-Transit', 'Delivered', 'RTO', 'Lost']

const cardSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '16px',
  boxShadow: SHADOW.card, p: 2.5, mb: 2.5,
}

export default function StatusTree({nodes = []}) {
  const {root, groups} = useMemo(() => {
    const list = Array.isArray(nodes) ? nodes : []
    const rootNode = list.find((n) => !n.parentId) || {title: 'Total Shipments', count: 0, id: null}
    const grp = list
      .filter((n) => n.parentId === rootNode.id)
      .map((g) => ({
        ...g,
        color: GROUP_COLORS[g.title] || PRIMARY_BLUE,
        children: list.filter((c) => c.parentId === g.id).sort((a, b) => b.count - a.count),
      }))
      .sort((a, b) => {
        const ia = GROUP_ORDER.indexOf(a.title), ib = GROUP_ORDER.indexOf(b.title)
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
      })
    return {root: rootNode, groups: grp}
  }, [nodes])

  const total = root.count || 0
  // Only show groups that actually have shipments — no "RTO: 0" clutter, and no dangling
  // tree when a flow (e.g. Reverse) has just one or two shipments.
  const visibleGroups = groups.filter((g) => g.count > 0)

  return (
    <Box sx={cardSx}>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY, mb: 0.3}}>
        Shipments Status Split
      </Typography>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 2.5}}>
        Every shipment grouped by where it is right now — bar shows each group's share of the total.
      </Typography>

      {total === 0 || visibleGroups.length === 0 ? (
        <Box sx={{textAlign: 'center', py: 4}}>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>No shipments in the selected range.</Typography>
        </Box>
      ) : (
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        {/* Total */}
        <Box sx={{bgcolor: '#2b3648', color: WHITE, borderRadius: '10px', px: 2.5, py: 1, textAlign: 'center', minWidth: 150}}>
          <Typography sx={{fontFamily: FONT, fontSize: '10px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em'}}>Total Shipments</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: '20px', fontWeight: WEIGHT.extra, lineHeight: 1.1}}>{total.toLocaleString()}</Typography>
        </Box>
        <Box sx={{width: '2px', height: 18, bgcolor: '#d5dbe4'}} />

        {/* Group cards */}
        <Box sx={{display: 'flex', gap: 1.75, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start', width: '100%'}}>
          {visibleGroups.map((g) => {
            const pct = total ? (g.count / total) * 100 : 0
            return (
              <Box key={g.id} sx={{width: 180, border: `1.5px solid ${g.color}`, bgcolor: withOpacity(g.color, 0.07), borderRadius: '12px', p: 1.5}}>
                <Typography sx={{fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.extra, textTransform: 'uppercase', letterSpacing: '0.03em', color: NAVY}}>
                  {g.title}
                </Typography>
                <Typography sx={{fontFamily: FONT, fontSize: '22px', fontWeight: WEIGHT.extra, color: g.color, lineHeight: 1.1}}>
                  {g.count.toLocaleString()}
                </Typography>
                {/* proportion bar */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, mb: g.children.length ? 1.25 : 0}}>
                  <Tooltip arrow title={`${g.title}: ${g.count.toLocaleString()} shipment${g.count === 1 ? '' : 's'} · ${pct.toFixed(1)}% of ${total.toLocaleString()} total`}>
                    <Box sx={{flex: 1, height: 6, bgcolor: withOpacity(g.color, 0.15), borderRadius: '4px', overflow: 'hidden', cursor: 'default'}}>
                      <Box sx={{height: '100%', width: `${pct}%`, bgcolor: g.color, borderRadius: '4px'}} />
                    </Box>
                  </Tooltip>
                  <Typography sx={{fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.bold, color: SUBTLE, fontVariantNumeric: 'tabular-nums'}}>{pct.toFixed(0)}%</Typography>
                </Box>
                {/* sub-statuses */}
                {g.children.length > 0 && (
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.6}}>
                    {g.children.map((c) => (
                      <Box key={c.id} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                        bgcolor: withOpacity(g.color, 0.06), border: `1px solid ${withOpacity(g.color, 0.22)}`,
                        borderRadius: '7px', px: 1, py: 0.5,
                      }}>
                        <Typography sx={{fontFamily: FONT, fontSize: '10px', color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{c.title}</Typography>
                        <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.bold, color: NAVY, fontVariantNumeric: 'tabular-nums'}}>{c.count.toLocaleString()}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      </Box>
      )}
    </Box>
  )
}
